/**
 * 클라이언트 상태 저장 단일 통로 — $j.Common.Store
 *
 * 그동안 쿠키 유틸이 3벌(common.js 전역함수 / F/Mobile/Theme.js 자체구현 / u-order-edit.js 인라인),
 * 스토리지 접근도 파일마다 제각각 try/catch 였다. 이 모듈로 수렴한다.
 *
 *   $j.Common.Store.cookie.get/set/remove(name, ...)
 *   $j.Common.Store.local.get/set/remove(key, ...)      // localStorage (JSON 직렬화 내장)
 *   $j.Common.Store.session.get/set/remove(key, ...)    // sessionStorage
 *
 * 규칙: .ai/state-conventions.md
 *  - 키 이름은 파일 상단 상수 또는 G5['CK_*'] 로. 리터럴 산발 금지.
 *  - 스토리지 접근은 항상 try/catch (일부 웹뷰는 저장소 접근 시 예외를 던진다).
 *  - 회원 종속 캐시는 키/값에 회원번호를 포함 (sessionStorage 는 탭 단위라 로그아웃 후에도 살아남음).
 *
 * new 로 생성하지 않는다 — 전부 static.
 */
export default class Store {

    /* ─────────────── 쿠키 ─────────────── */
    // document.cookie 기반. HttpOnly 서버쿠키는 여기서 못 읽는다(그게 정상 — 서버 전용).
    static cookie = {
        /**
         * @param {string} name
         * @returns {string|null} 없으면 null
         */
        get(name) {
            const nameEQ = name + '=';
            const parts = document.cookie ? document.cookie.split(';') : [];
            for (let c of parts) {
                while (c.charAt(0) === ' ') c = c.substring(1);
                if (c.indexOf(nameEQ) === 0) {
                    const raw = c.substring(nameEQ.length);
                    // decode 하되, 과거 encode 없이 저장된 값('50%' 등)은 decodeURIComponent 가 throw 하므로 raw 로 폴백.
                    try { return decodeURIComponent(raw); } catch (e) { return raw; }
                }
            }
            return null;
        },

        /**
         * @param {string} name
         * @param {string} value
         * @param {Object} [opt] {days:number, path:string}
         *   days 미지정 = 세션쿠키(탭 닫으면 소멸). path 기본 '/'.
         *   ⚠️ Secure/HttpOnly/SameSite 는 JS 에서 신뢰성 있게 못 건다 — 그런 속성이 필요한 쿠키는 서버(CookieJar)가 굽는다.
         */
        set(name, value, opt = {}) {
            // 값은 encodeURIComponent (한글 폰트명·공백 등 안전 저장). get 이 대칭으로 decode 한다.
            // PHP 는 $_COOKIE 를 자동 URL-decode 하므로 경계쿠키(RMB·PCFont)도 이 인코딩이 맞다.
            let str = name + '=' + encodeURIComponent(value == null ? '' : value) + '; path=' + (opt.path || '/');
            if (opt.days) {
                const d = new Date();
                d.setTime(d.getTime() + opt.days * 24 * 60 * 60 * 1000);
                str += '; expires=' + d.toUTCString();
            }
            document.cookie = str;
        },

        /**
         * 쿠키 삭제. path 는 만든 때와 같아야 지워진다(기본 '/').
         * @param {string|null} path  null 이면 path 속성을 생략(현재 문서 경로에서 삭제 — 레거시 path-less 쿠키용).
         */
        remove(name, path = '/') {
            const p = (path === null) ? '' : '; path=' + path;
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC' + p + ';';
        },

        /**
         * 현재 문서에서 보이는 쿠키 이름 목록. 접두어로 흩어진 레거시 쿠키를 훑을 때 쓴다.
         * (값이 필요하면 이름으로 get.) document.cookie 직접 파싱을 이 한 곳으로 모은다.
         * @returns {string[]}
         */
        names() {
            const out = [];
            (document.cookie ? document.cookie.split(';') : []).forEach((c) => {
                const eq = c.indexOf('=');
                const k = (eq < 0 ? c : c.slice(0, eq)).trim();
                if (k) out.push(k);
            });
            return out;
        },
    };

    /* ─────────────── localStorage ─────────────── */
    // get/set 은 원시 문자열 그대로다 (native getItem/setItem 과 같은 의미 + try/catch).
    // 코드 대부분이 '0'/'1'/'fold' 같은 문자열을 정확비교(=== '1')하므로 자동 JSON 파싱을 하지 않는다.
    // 객체를 담을 땐 getJSON/setJSON 을 쓴다.
    static local = {
        /** @returns {string|null} 값(문자열) 또는 없음/차단 시 fallback(기본 null) */
        get(key, fallback = null) { return Store._get(window.localStorage, key, fallback); },
        set(key, value) { return Store._set(window.localStorage, key, value); },
        remove(key) { Store._del(window.localStorage, key); },
        /** JSON 파싱해서 반환. 없음/파싱실패 시 fallback. */
        getJSON(key, fallback = null) { return Store._getJSON(window.localStorage, key, fallback); },
        setJSON(key, value) { return Store._set(window.localStorage, key, JSON.stringify(value)); },
    };

    /* ─────────────── sessionStorage ─────────────── */
    static session = {
        get(key, fallback = null) { return Store._get(window.sessionStorage, key, fallback); },
        set(key, value) { return Store._set(window.sessionStorage, key, value); },
        remove(key) { Store._del(window.sessionStorage, key); },
        getJSON(key, fallback = null) { return Store._getJSON(window.sessionStorage, key, fallback); },
        setJSON(key, value) { return Store._set(window.sessionStorage, key, JSON.stringify(value)); },
    };

    /* ─────────────── 내부 (localStorage/sessionStorage 공용) ─────────────── */
    // 일부 웹뷰(시크릿·저장소 차단)는 접근 자체가 throw → 전부 try/catch 로 감싼다.
    static _get(store, key, fallback) {
        try { const v = store.getItem(key); return v === null ? fallback : v; }
        catch (e) { return fallback; }
    }

    static _set(store, key, value) {
        try { store.setItem(key, value == null ? '' : String(value)); return true; }
        catch (e) { return false; }   // 용량초과·접근불가
    }

    static _del(store, key) {
        try { store.removeItem(key); } catch (e) { /* 무시 */ }
    }

    static _getJSON(store, key, fallback) {
        try {
            const raw = store.getItem(key);
            if (raw === null) return fallback;
            try { return JSON.parse(raw); } catch (e) { return fallback; }
        } catch (e) { return fallback; }
    }
}
